if ( typeof ProductImageZoom !== 'function' ) {
	class ProductImageZoom extends HTMLElement {

		static NAV_PREV_SVG = '<svg fill="none" height="13" viewBox="0 0 8 13" width="8" xmlns="http://www.w3.org/2000/svg"><g fill="currentColor"><path d="m7.91419 1.41431-6.48529 6.48528-1.4142102-1.41422 6.4852802-6.48527702z"/><path d="m6.48528 12.9849-6.48528027-6.48532 1.41421027-1.41421 6.48528 6.48523z"/></g></svg>';
		static NAV_NEXT_SVG = '<svg width="9" height="13" viewBox="0 0 9 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.914169 11.5857L7.39945 5.10041L8.81367 6.51463L2.32838 12.9999L0.914169 11.5857Z" fill="currentColor"/><path d="M2.34307 0.0151367L8.82835 6.50042L7.41414 7.91463L0.928857 1.42935L2.34307 0.0151367Z" fill="currentColor"/></svg>';
		static CLOSE_SVG = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M17 1L1 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M1 1L17 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

		static _getSettings() {
			const settings = window.KROWN?.settings || {};
			return {
				zoomLoader: settings.symbols?.zoom_loader || '',
				prevLabel: settings.locales?.slider_prev_button_label || 'Previous',
				nextLabel: settings.locales?.slider_next_button_label || 'Next'
			};
		}

		static _ensureZoomRoot() {
			let zoom = document.getElementById('zoom');
			if ( zoom && zoom.dataset.build === '3' && zoom.querySelector('.zoom__stage .zoom__nav--prev') ) {
				return zoom;
			}
			if ( zoom ) {
				zoom.remove();
			}

			const cfg = ProductImageZoom._getSettings();

			zoom = document.createElement('div');
			zoom.id = 'zoom';
			zoom.dataset.build = '3';
			zoom.innerHTML = `
				<div class="zoom__backdrop" aria-hidden="true"></div>
				<div class="zoom__dialog" role="dialog" aria-modal="true" aria-label="Product image preview">
					<button type="button" class="zoom__exit" aria-label="Close">${ProductImageZoom.CLOSE_SVG}</button>
					<div class="zoom__stage">
						<button type="button" class="zoom__nav zoom__nav--prev" aria-label="${cfg.prevLabel}">
							${ProductImageZoom.NAV_PREV_SVG}
						</button>
						<img alt="" />
						<span class="zoom__loader">${cfg.zoomLoader}</span>
						<button type="button" class="zoom__nav zoom__nav--next" aria-label="${cfg.nextLabel}">
							${ProductImageZoom.NAV_NEXT_SVG}
						</button>
					</div>
				</div>
			`;
			document.body.append(zoom);

			const closeZoom = () => ProductImageZoom.activeInstance?._productZoomUnmount();

			zoom.querySelector('.zoom__backdrop').addEventListener('click', closeZoom);
			zoom.querySelector('.zoom__exit').addEventListener('click', closeZoom);
			zoom.querySelector('.zoom__dialog').addEventListener('click', (e) => e.stopPropagation());
			zoom.querySelector('.zoom__nav--prev').addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				ProductImageZoom.activeInstance?._goToZoomSlide('prev');
			});
			zoom.querySelector('.zoom__nav--next').addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				ProductImageZoom.activeInstance?._goToZoomSlide('next');
			});

			if ( ! ProductImageZoom._keydownBound ) {
				document.addEventListener('keydown', ProductImageZoom._onKeydown);
				ProductImageZoom._keydownBound = true;
			}

			return zoom;
		}

		static _onKeydown(e) {
			const instance = ProductImageZoom.activeInstance;
			const zoom = document.getElementById('zoom');
			if ( ! instance || ! zoom?.classList.contains('opened') ) {
				return;
			}

			if ( e.key === 'Escape' ) {
				e.preventDefault();
				instance._productZoomUnmount();
			} else if ( e.key === 'ArrowLeft' ) {
				e.preventDefault();
				instance._goToZoomSlide('prev');
			} else if ( e.key === 'ArrowRight' ) {
				e.preventDefault();
				instance._goToZoomSlide('next');
			}
		}

		constructor() {
			super();
			this.zoom = ProductImageZoom._ensureZoomRoot();
			this.zoomPrev = this.zoom.querySelector('.zoom__nav--prev');
			this.zoomNext = this.zoom.querySelector('.zoom__nav--next');
			this._loadTimeout = null;

			this.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				ProductImageZoom.activeInstance = this;
				ProductImageZoom.zoomSlides = this._getZoomSlides();
				const galleryItem = this.closest('.product-gallery-item');
				const galleryIndex = galleryItem ? parseInt(galleryItem.dataset.index, 10) : 0;
				this.zoomSlideIndex = ProductImageZoom.zoomSlides.findIndex(slide => slide.galleryIndex === galleryIndex);
				if ( this.zoomSlideIndex < 0 ) {
					this.zoomSlideIndex = 0;
				}
				this._openZoomSlide(this.zoomSlideIndex);
			});
		}

		_getGallery() {
			return this.closest('[data-js-product-gallery]');
		}

		_getZoomSlides() {
			const gallery = this._getGallery();
			const slides = [];

			if ( ! gallery ) {
				return [{
					galleryIndex: parseInt(this.closest('.product-gallery-item')?.dataset.index || 0, 10),
					src: this.getAttribute('data-image')
				}];
			}

			gallery.querySelectorAll('.product-gallery-item').forEach(item => {
				const zoomEl = item.querySelector('product-image-zoom');
				if ( zoomEl ) {
					slides.push({
						galleryIndex: parseInt(item.dataset.index, 10),
						src: zoomEl.getAttribute('data-image')
					});
				}
			});

			return slides.length ? slides : [{
				galleryIndex: parseInt(this.closest('.product-gallery-item')?.dataset.index || 0, 10),
				src: this.getAttribute('data-image')
			}];
		}

		_syncGallery(galleryIndex) {
			const gallery = this._getGallery();
			const root = gallery?.closest('product-page') || gallery?.parentElement;
			if ( ! root ) {
				return;
			}

			const slider = root.querySelector('css-slider');
			if ( slider && slider.sliderEnabled ) {
				slider.changeSlide(galleryIndex);
			}

			root.querySelectorAll('.product-gallery__thumbnails .thumbnail').forEach(thumb => {
				thumb.classList.toggle('active', parseInt(thumb.dataset.index, 10) === galleryIndex);
			});
		}

		_updateZoomNav() {
			const showNav = ProductImageZoom.zoomSlides.length > 1;
			this.zoom.classList.toggle('zoom--has-nav', showNav);
		}

		_markLoaded() {
			if ( ProductImageZoom.activeInstance !== this ) {
				return;
			}
			clearTimeout(this._loadTimeout);
			this.zoom.classList.add('loaded');
		}

		_openZoomSlide(slideIndex) {
			const slide = ProductImageZoom.zoomSlides[slideIndex];
			if ( ! slide ) {
				return;
			}

			this.zoomSlideIndex = slideIndex;
			this.image = this.zoom.querySelector('.zoom__stage img');
			this.zoom.classList.add('opened');
			this.zoom.classList.remove('loaded');
			document.body.classList.add('zoom-open');
			this._updateZoomNav();
			this._syncGallery(slide.galleryIndex);

			clearTimeout(this._loadTimeout);

			const finish = () => this._markLoaded();

			this.image.onload = finish;
			this.image.onerror = finish;
			this.image.src = slide.src;

			if ( this.image.decode ) {
				this.image.decode().then(finish).catch(finish);
			}

			if ( this.image.complete ) {
				finish();
			}

			this._loadTimeout = setTimeout(finish, 400);
		}

		_goToZoomSlide(direction) {
			const total = ProductImageZoom.zoomSlides.length;
			if ( total <= 1 ) {
				return;
			}

			let slideIndex = this.zoomSlideIndex;
			if ( direction === 'next' ) {
				slideIndex = (slideIndex + 1) % total;
			} else if ( direction === 'prev' ) {
				slideIndex = (slideIndex - 1 + total) % total;
			} else {
				slideIndex = parseInt(direction, 10);
			}

			this._openZoomSlide(slideIndex);
		}

		_productZoomUnmount() {
			clearTimeout(this._loadTimeout);
			this.zoom.classList.remove('opened', 'loaded', 'zoom--has-nav');
			document.body.classList.remove('zoom-open');

			const image = this.zoom.querySelector('.zoom__stage img');
			if ( image ) {
				image.onload = null;
				image.onerror = null;
				image.removeAttribute('src');
			}

			ProductImageZoom.activeInstance = null;
		}

	}

	if ( typeof customElements.get('product-image-zoom') == 'undefined' ) {
		customElements.define('product-image-zoom', ProductImageZoom);
	}

}
